import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/Combobox'
import { type ControlSize } from '@/components/ui/control'
import { useSession } from '@/features/auth/api/useSession'
import {
  useCounterparties,
  useCreateCounterparty,
} from '@/features/counterparties/api/useCounterparties'
import {
  CounterpartyFormSheet,
  type CounterpartyDraft,
} from '@/features/counterparties/components/CounterpartyFormSheet'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import { counterpartySaveErrorMessage } from '@/features/directories/directoryText'
import type { CounterpartyCategory } from '@/types/domain'

/* A counterparty picked from the directory, by id — the strict picker (S-22b,
 * extracted in S-22d). `CarrierSelect` is this control over the three
 * categories that move cargo; the case cost line and «Оплатить фуру»'s payee
 * use it over all six. VendorPicker — the quote costing's free text — is a
 * different control on purpose and does not share this one.
 *
 * ── What it emits ──
 *
 * `{ id, name }`, both halves together, always. `id` is a directory row's id,
 * or '' when the value is NOT a directory row: nothing picked, or the stored
 * "as entered" name re-picked. That '' is the whole of "is this a directory
 * row?" — a caller that requires one (the cost line, the payee) tests the id,
 * never the name.
 *
 * ── The CATEGORY is shown on every row ──
 *
 * One name can be two records — a haulier and the forwarding desk of the same
 * group — and the category is how the reader tells them apart.
 *
 * ── What is offered, and what merely stays selected ──
 *
 * The list offers ACTIVE counterparties (the list read's default). A
 * deactivated one is one the desk has decided not to work with again, and a
 * chooser that kept offering it would make deactivation cosmetic.
 *
 * The value already on the record is nonetheless kept SELECTED, because
 * nothing here may drop data that is already saved:
 *
 *   * a linked counterparty that is now inactive, outside these categories, or
 *     missing because the list came back EMPTY — a reader without
 *     `counterparty.read` gets `[]`. The trigger shows the stored name, and the
 *     id leaves exactly as it came: this control writes only on a pick.
 *   * a free-text name matching no counterparty at all — a leg recorded before
 *     migration 0046, a cost line booked by name before S-22d. It is offered as
 *     its own first row and marked "as entered", which is exactly what
 *     CountryDropdown does with a port name stored before that field became a
 *     picker. A caller that requires a directory row shows it and refuses the
 *     save until a pick (the sanctioned re-pick, `quality/invariants.md`): the
 *     value is shown, never dropped.
 *
 * ── The add-a-counterparty escape hatch ──
 *
 * Inside the picker rather than beside it, and it is the difference between
 * this control being used and being worked around. The company standing in
 * front of somebody at 7pm is frequently not in the directory yet, and a picker
 * that makes them abandon the form, cross to the directory screen and start
 * again is a picker whose users save with nobody named. The host sheet stays
 * open and holding its draft throughout — the counterparty form is a second
 * sheet over the first, not a navigation. Offered only to a reader who may
 * create one (`counterparty.read` + `counterparty.create`): a button the
 * server would refuse is not shown (frontend/README).
 *
 * ── Pinned rows (S-22d2) ──
 *
 * A host may put its own choices ABOVE the directory's — the outbound payment's
 * «Клиент работы», a refund's party, which is a client record and not a
 * counterparty. They are optional and change nothing for a host that passes
 * none. A pinned row is not a counterparty, so picking one calls `onPin` with
 * the row's own id rather than `onChange`, and a pinned row the host says is
 * selected (`pinnedValue`) outranks `value` and the "as entered" row.
 */

/** The value the "as entered" row carries.
 *
 *  A sentinel rather than the name itself, so that `onChange` can tell
 *  "the user re-picked the stored value" from "the user picked a real
 *  counterparty". It cannot collide with any row beside it because every other
 *  id in this list is a counterparty UUID and this is not one.
 *
 *  It was written with a leading NUL, on the reasoning that no real value can
 *  contain one. True, and the wrong trade: a NUL anywhere in a file makes git
 *  treat the WHOLE file as binary, so this component had no readable diff in
 *  review — a large price for a guarantee "not a UUID" already gives. */
const AS_ENTERED = 'as-entered'

/** No pinned rows — one constant, so a default does not mint a new array on
 *  every render. */
const NO_PINNED: readonly CounterpartyPickerPinnedRow[] = []

/** The words a host puts on the control — already translated, because each
 *  host speaks its own glossary («перевозчик» on a leg, «контрагент» on a
 *  cost line). */
export interface CounterpartyPickerStrings {
  /** The empty trigger. */
  placeholder: string
  /** The filter at the top of the list. */
  search: string
  /** The hint on the stored "as entered" row. */
  asEntered: string
  /** The hint on a stored counterparty that is now deactivated. */
  inactive: string
  /** The add-a-new-row button under the list. */
  add: string
  /** The create sheet's title; omitted, the section's own. */
  createTitle?: string
}

/** A host's own row above the directory's (S-22d2). The id must not be a
 *  counterparty's: the payment's are `client:<uuid>`. */
export interface CounterpartyPickerPinnedRow {
  id: string
  label: string
  hint: string
  /** Offered but not pickable — the host's `hint` says why (S-22f: a refund
   *  row on a payment already allocated to costs). */
  disabled?: boolean
}

export interface CounterpartyPickerProps {
  /** The linked counterparty's id, or '' when the record links nobody. */
  value: string
  /** The name as the record carries it — the linked counterparty's, or the
   *  free text from before the link. What the trigger shows when the list has
   *  no row for `value`, and what the "as entered" row offers. */
  name: string
  /** Both halves move together, always. `id` is '' exactly when the value is
   *  not a directory row. */
  onChange: (counterparty: { id: string; name: string }) => void
  /** What the list offers and the create sheet may create — the same set, so
   *  the form cannot create something the picker would then refuse to show. */
  categories: readonly CounterpartyCategory[]
  strings: CounterpartyPickerStrings
  id?: string
  disabled?: boolean
  size?: ControlSize
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** The control's name where no `<label>` is bound to `id` — a carrier cell
   *  in a grid row («Перевозчик, фура 2») names itself (S-10). */
  'aria-label'?: string
  /** Rows offered first, before the directory's. Optional: without them the
   *  picker is exactly the strict counterparty picker. */
  pinned?: readonly CounterpartyPickerPinnedRow[]
  /** The pinned row the record holds, by its id; it outranks `value`. */
  pinnedValue?: string
  /** A pinned row was picked. */
  onPin?: (id: string) => void
}

/* Base UI: none directly — composed from Combobox.tsx, which is the
   `@base-ui/react` combobox primitive wearing this system's tokens. */
export function CounterpartyPicker({
  value,
  name,
  onChange,
  categories,
  strings,
  id,
  disabled,
  size = 'md',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
  pinned = NO_PINNED,
  pinnedValue,
  onPin,
}: CounterpartyPickerProps) {
  const { t } = useTranslation()

  /* One read of the host's categories — the endpoint takes `category`
     repeated, so rows outside them are never fetched only to be dropped here.
     An ordinary cached query: the second sheet opened in a session issues
     none. */
  const listed = useCounterparties({ categories })
  const { data: session } = useSession()
  const mayCreate = counterpartyRights(session).edit

  const createCounterparty = useCreateCounterparty()
  const [adding, setAdding] = useState(false)

  /* The list's own open state, held here rather than left to the primitive,
     for ONE reason: the add-a-counterparty button opens a second sheet, and a
     combobox popup left open sits ABOVE it. Both are portaled to <body>, the
     popup mounts last, and no z-index the sheet can carry wins against a
     sibling later in document order — so the counterparty form rendered behind
     its own trigger's list, which is what the user saw.

     Closing the list is also the right thing independently of stacking: the
     question it asks ("which of these?") has been answered with "none of them,
     make a new one", and leaving it open behind a form would offer a choice
     that is about to be made for the user anyway. */
  const [listOpen, setListOpen] = useState(false)

  const all = listed.data ?? []
  /* Active, plus whoever is already on the record. Sorted by name across the
     categories rather than grouped: the reader knows the company's name and is
     looking for it, and grouping would make them decide which list to scan
     before they could start. */
  const offered = all
    .filter((counterparty) => counterparty.isActive || counterparty.id === value)
    .sort((a, b) => a.name.localeCompare(b.name))

  /** A pinned row the record holds — then `name` is that row's, never typed. */
  const pinnedSelected =
    pinnedValue !== undefined && pinned.some((row) => row.id === pinnedValue)

  /** A saved name that is not a link: pre-0046 on a leg, pre-S-22d on a cost. */
  const asEntered = !pinnedSelected && value === '' && name.trim() !== ''

  const rows = [
    ...pinned.map((row) => ({
      id: row.id,
      label: row.label,
      hint: row.hint,
      search: `${row.label} ${row.hint}`.toLowerCase(),
      disabled: row.disabled,
    })),
    ...(asEntered
      ? [
          {
            id: AS_ENTERED,
            label: name.trim(),
            hint: strings.asEntered,
            search: name.trim().toLowerCase(),
          },
        ]
      : []),
    ...offered.map((counterparty) => ({
      id: counterparty.id,
      label: counterparty.name,
      hint: counterparty.isActive
        ? t(`counterpartyCategory.${counterparty.category}`)
        : strings.inactive,
      search: `${counterparty.name} ${t(`counterpartyCategory.${counterparty.category}`)}`.toLowerCase(),
    })),
  ]

  const byID = new Map(rows.map((row) => [row.id, row]))

  /* What Base UI is holding. The sentinel stands in for the stored name, so
     the control shows a selected row rather than an empty box over a record
     that does say who it was. */
  const selected = pinnedSelected
    ? (pinnedValue ?? null)
    : value !== ''
      ? value
      : asEntered
        ? AS_ENTERED
        : null

  const pick = (next: string | null) => {
    if (next === null) {
      onChange({ id: '', name: '' })
      return
    }
    const pinnedRow = pinned.find((row) => row.id === next)
    if (pinnedRow) {
      if (!pinnedRow.disabled) onPin?.(next)
      return
    }
    if (next === AS_ENTERED) {
      // Re-picking the stored row changes nothing, deliberately: it keeps the
      // free text and does not invent a link the record never had.
      onChange({ id: '', name: name.trim() })
      return
    }
    const counterparty = all.find((row) => row.id === next)
    onChange({ id: next, name: counterparty?.name ?? '' })
  }

  return (
    <>
      <Combobox
        items={rows.map((row) => row.id)}
        value={selected}
        onValueChange={pick}
        itemToStringLabel={(rowID: string) => byID.get(rowID)?.label ?? ''}
        /* One keystroke matches the name AND the category, so «водитель» finds
           the owner-drivers when somebody is looking for a kind of company
           rather than for one they can already name. The default filter sees
           only the label. */
        filter={(rowID: string, query: string) => {
          const needle = query.trim().toLowerCase()
          if (needle === '') return true
          return byID.get(rowID)?.search.includes(needle) ?? false
        }}
        disabled={disabled}
        autoHighlight
        open={listOpen}
        onOpenChange={setListOpen}
      >
        <ComboboxTrigger
          id={id}
          triggerSize={size}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          aria-label={ariaLabel}
        >
          <ComboboxValue>
            {(rowID: string | null) => {
              /* `name` rather than the row's label, and the fallback matters:
                 the list is still in flight on the first paint of a sheet
                 opened on a saved record — or came back empty for a reader who
                 may not read counterparties — so `byID` has no row and a
                 label-only trigger would show the placeholder over a record
                 that names somebody. */
              const label = (rowID ? byID.get(rowID)?.label : '') || name.trim()
              if (!label) {
                return <span className="text-text-faint truncate">{strings.placeholder}</span>
              }
              return <span className="text-text-body truncate">{label}</span>
            }}
          </ComboboxValue>
        </ComboboxTrigger>

        <ComboboxContent>
          <ComboboxSearch placeholder={strings.search} />

          <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
            {t('common.form.noMatches')}
          </ComboboxEmpty>

          <ComboboxList>
            {(rowID: string) => {
              const row = byID.get(rowID)
              if (!row) return null
              return (
                <ComboboxItem
                  key={rowID}
                  value={rowID}
                  disabled={'disabled' in row && row.disabled === true}
                >
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {/* The category, one step down and muted — the secondary
                      reading of the same row rather than a second label,
                      exactly as the alpha-2 code reads on a country row. */}
                  <span className="text-text-muted shrink-0 text-[12px]">
                    {row.hint}
                  </span>
                </ComboboxItem>
              )
            }}
          </ComboboxList>

          {/* Pinned under the list rather than offered as a row in it: it is not
              a counterparty, and a filter that hid it when nothing matched would
              hide it exactly when it is most wanted. A plain button, not a
              ComboboxItem, so keyboard navigation of the list does not land on
              something that is not a choice. */}
          {mayCreate ? (
            <div className="border-border-subtle -mx-1 -mb-1 mt-1 border-t px-1 pt-1">
              <button
                type="button"
                onClick={() => {
                  // The last attempt's refusal belongs to the last attempt.
                  createCounterparty.reset()
                  setListOpen(false)
                  setAdding(true)
                }}
                className="text-brand hover:bg-surface-hover flex w-full cursor-pointer items-center gap-1.5 rounded-[5px] px-2 py-1.5 text-left text-[12.5px]"
              >
                <Plus size={14} aria-hidden />
                {strings.add}
              </button>
            </div>
          ) : null}
        </ComboboxContent>
      </Combobox>

      {/* The counterparty editor, over the host sheet rather than instead of it.
          The host draft is untouched — this sheet is a sibling in the same React
          tree, and `useFormDraft` re-seeds only when the sheet it belongs to
          closes or changes record.

          `categories` is the same set the picker lists, so the form cannot
          create something the picker would then refuse to show; the reader
          chooses one, there is no default. */}
      {mayCreate ? (
        <CounterpartyFormSheet
          open={adding}
          onClose={() => setAdding(false)}
          title={strings.createTitle}
          categories={categories}
          pending={createCounterparty.isPending}
          /* Worded inside the sheet: the picker behind it is under the scrim,
             so a refusal anywhere else is a Save that seems to do nothing. */
          error={
            createCounterparty.error
              ? counterpartySaveErrorMessage(createCounterparty.error, t)
              : undefined
          }
          onSubmit={(draft: CounterpartyDraft & { category: CounterpartyCategory }) =>
            createCounterparty.mutate(
              {
                category: draft.category,
                name: draft.name.trim(),
                taxId: draft.taxId.trim(),
                registrationId: draft.registrationId.trim(),
                country: draft.country.trim(),
                address: draft.address.trim(),
                contacts: draft.contacts,
              },
              {
                // Selected on the record the moment it exists, which is the
                // whole point of the escape hatch: somebody who has just typed
                // a company's name must not have to find it in a list
                // afterwards.
                onSuccess: (created) => {
                  onChange({ id: created.id, name: created.name })
                  setAdding(false)
                },
              },
            )
          }
        />
      ) : null}
    </>
  )
}
